import {
	AfterViewInit,
	Component,
	Input,
	OnDestroy,
	OnInit,
	ViewChild
} from '@angular/core';
import { InvoicesDataProvider } from '@services/invoice/invoices-data-service/invoices.data.service';
import { MessageService } from '@services/helpers/message-service/message.service';
import {
	CurrencyAndTaxes,
	DataToGetInvoice,
	Invoice,
	InvoiceDataToSave,
	InvoiceIdAndDate,
	IssueDateFormat,
	MainInfoData,
	Payment,
	PaymentSchedule
} from '@web-components/invoices/invoice.model';
import {
	Contract,
	ContractQuestionData,
	Job,
	ListItem,
	Package,
	Product,
	Question,
	Summary
} from '@models/models';
import { finalize, map, takeUntil, filter } from 'rxjs/operators';
import { Subject } from 'rxjs/Subject';
import { InvoiceServiceProvider } from '@services/invoice/invoice-service/invoice.service';
import { QuotesDataProvider } from '@services/quote/quotes-data-service/quotes.data.service';
import { Observable } from 'rxjs/Observable';
import { JobsDataServiceProvider } from '@services/job/jobs.data.service';
import { SettingsProvider } from '@services/settings/settings';
import { TemplatesDataProvider } from '@services/data-services/templates-data-service/templates.data.service';
import { ContractsDataProvider } from '@services/contract/contracts.data.service';
import { PaymentSchedulesDataProvider } from '@services/payments/payments-schedules-data-service/payments-schedules.data.service';
import { QuoteUpdateService } from '@services/quote/quote-update.service';
import { ProductPackagesDataServiceProvider } from '@services/settings/product-packages.data.service';
import { Location } from '@angular/common';
import { PaymentScheduleComponent } from '@web-components/quotes/common/payment-schedule/payment-schedule';
import { TimeServiceProvider } from '@services/helpers/time-service/time.service';
import { StringModifierServiceProvider } from '@services/helpers/string-modifier-service/stringModifier.service';

@Component({
	selector: 'invoice-update',
	template: require('./invoice-update.html'),
	styles: [require('./invoice-update.scss')]
})
export class InvoiceUpdateComponent
	implements OnInit, AfterViewInit, OnDestroy {
	@Input() dataToGetInvoice: DataToGetInvoice;
	@ViewChild('paymentScheduleComponent')
	paymentScheduleComponent: PaymentScheduleComponent;

	invoice: Invoice = <Invoice>{};
	job: Job;

	editInvoice: boolean;
	chosenPackages: Array<Package> = [];
	datePlaceholder: string;

	// split data from invoice DTO
	issueDate: IssueDateFormat;
	poNumber: number;
	contractQuestionData: ContractQuestionData;
	currencyAndTax: CurrencyAndTaxes;
	payments: Array<Payment> = [];
	paymentsFromServer: Array<Payment> = [];
	paymentSchedule: PaymentSchedule;
	contract: Contract;
	questionnaire: Question;
	paymentScheduleNames;
	selectedPaymentSchedule;
	productsNames: Array<ListItem> = [];
	discountsNames: Array<ListItem> = [];
	products: Array<Product> = [];
	discounts: Array<Product> = [];
	summary: Summary;

	// IDs
	leadId: number;
	jobId: number;
	paymentScheduleTemplateId: number;
	paymentScheduleIdFromServer: number;

	// Data to save
	questionnaireToSaveId: string = null;
	contractToSave: Contract = null;
	notesToSave: string = null;
	paymentsToSave: Array<Payment> = [];
	mainInfoDataToSave: MainInfoData;

	buttonDisable: boolean;
	private unsubscribe = new Subject();

	constructor(
		private invoicesDataService: InvoicesDataProvider,
		private messageService: MessageService,
		private invoiceService: InvoiceServiceProvider,
		private quoteDataService: QuotesDataProvider,
		private jobDataService: JobsDataServiceProvider,
		private settingsService: SettingsProvider,
		private templatesDataService: TemplatesDataProvider,
		private contractDataService: ContractsDataProvider,
		private paymentsSchedulesDataService: PaymentSchedulesDataProvider,
		private productPackagesDataService: ProductPackagesDataServiceProvider,
		private quoteUpdateService: QuoteUpdateService,
		private stringModifierService: StringModifierServiceProvider,
		private timeService: TimeServiceProvider,
		private location: Location
	) {}

	ngOnInit(): void {
		if (this.dataToGetInvoice.invoiceID && this.dataToGetInvoice.isJob) {
			this.jobId = this.dataToGetInvoice.id;
			this.getInvoiceSourceData(this.dataToGetInvoice.invoiceID);
			this.editInvoice = true;
		} else if (
			this.dataToGetInvoice.invoiceID &&
			!this.dataToGetInvoice.isJob
		) {
			this.getJobId(true);
			this.editInvoice = true;
		} else if (
			!this.dataToGetInvoice.invoiceID &&
			this.dataToGetInvoice.isJob
		) {
			this.jobId = this.dataToGetInvoice.id;
			this.showNewInvoice(this.jobId);
		} else {
			this.getJobId(false);
		}
		this.getProductsTemplates();
		this.getTimeSettings();
		this.getPaymentsSchedules();
	}

	ngAfterViewInit(): void {
		this.registerUpdateProductSubscription();
	}

	ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
		this.quoteUpdateService.setNextUpdateProduct(null, null);
	}

	addProduct(): void {
		this.products.push(QuoteUpdateService.setEmptyProduct('ADD_ON'));
		this.updateInvoiceSummary(true);
	}

	addNewDiscount(): void {
		this.discounts.push(QuoteUpdateService.setEmptyProduct('GIFT'));
		this.updateInvoiceSummary(true);
	}

	saveInvoice(): void {
		this.buttonDisable = true;
		if (!this.validateInvoice()) {
			this.buttonDisable = false;
			return;
		}

		const data = this.gatherDataToSaveInvoice();

		if (this.editInvoice) {
			this.updateInvoice(data);
		} else {
			this.createInvoice(data);
		}
	}

	back(): void {
		this.location.back();
	}

	setQuestionnaire(questionnaireId: string): void {
		this.questionnaireToSaveId = questionnaireId;
	}

	setContract(contract: Contract): void {
		this.contractToSave = contract;
	}

	disableButtons(isDisable: boolean): void {
		this.buttonDisable = isDisable;
	}

	setPaymentScheduleId(payments): void {
		this.paymentScheduleTemplateId = payments.id;
		this.paymentsToSave = payments.payments;
	}

	onProductChange(): void {
		this.updateInvoiceSummary(true);
	}

	private registerUpdateProductSubscription(): void {
		this.quoteUpdateService.updatedProductObservable
			.pipe(
				takeUntil(this.unsubscribe),
				filter((product): boolean => product !== null)
			)
			.subscribe(updatedProduct => {
				if (
					this.quoteUpdateService.validateProductsPropsDefined(this.products)
				) {
					this.updateInvoiceSummary(true);
				}
			});
	}

	private updateInvoiceProductProperties(product): void {
		this.products.forEach((packageItem, index) => {
			if (index === product.index) {
				this.products[index].price = product.product.price;
				this.products[index].quantity = product.product.quantity;
				this.products[index].discount = product.product.discount;
				this.products[index].taxType = product.product.taxType;
				this.products[index].amount = product.product.amount;
			}
		});
	}

	private showNewInvoice(jobId: number): void {
		this.paymentScheduleTemplateId = -1;
		this.selectedPaymentSchedule = null;
		this.setInvoiceIdAndDate(jobId);

		this.getNewInvoice();

		this.getCurrencyAndTaxes();

		this.getContractAndQuestionnaireInfo();
	}

	private getJobId(makeRequest: boolean): void {
		this.leadId = this.dataToGetInvoice.id;
		this.quoteDataService
			.getJobId(this.leadId)
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((data: number) => {
				this.jobId = data;
				this.dataToGetInvoice.id = this.jobId;
				if (makeRequest) {
					this.getInvoiceSourceData(this.dataToGetInvoice.invoiceID);
				} else {
					this.showNewInvoice(this.jobId);
				}
			});
	}

	private getInvoiceSourceData(id: number): void {
		this.invoicesDataService
			.getInvoiceV1(id)
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(
				(data: Invoice): void => {
					this.successGetInvoiceData(data);
				},
				(error): void => this.messageService.showDefaultErrorMessage(error)
			);
	}

	private successGetInvoiceData(invoice: Invoice): void {
		this.invoice = JSON.parse(JSON.stringify(invoice));
		this.job = this.invoice.job;

		if (invoice.paymentSchedule && invoice.paymentSchedule.id) {
			this.job.paymentScheduleId = invoice.paymentSchedule.id;
		}

		this.setMainInfoData(
			invoice.invoiceId,
			this.quoteUpdateService.setQuoteIssueDate(invoice.issueDate),
			this.invoice.poNumber
		);

		this.setInvoiceSources(invoice);
	}

	private setInvoiceSources(invoice: Invoice): void {
		const {
			contract,
			currencyAndTaxes,
			paymentSchedule,
			payments,
			questionnaire
		} = invoice;

		this.currencyAndTax = currencyAndTaxes;
		if (paymentSchedule === null) {
			this.paymentScheduleTemplateId = -1;
		} else {
			this.paymentScheduleIdFromServer = paymentSchedule.id;
		}
		this.payments = this.addMissDateField(payments);
		this.paymentsFromServer = payments;
		this.paymentSchedule = paymentSchedule;

		if (contract !== null) {
			this.contract = contract;
		}
		if (questionnaire !== null) {
			this.questionnaireToSaveId = questionnaire.id;
			this.questionnaire = questionnaire;
		}

		const {
			addons,
			discounts
		} = QuoteUpdateService.separateDiscountsAndProducts([
			...invoice.summary.items
		]);
		this.products = addons;
		this.discounts = discounts;

		this.notesToSave = invoice.notes;
		this.summary = invoice.summary;

		this.getContractAndQuestionnaireInfo();
	}

	private updateInvoiceSummary(needToUpdatePayments): void {
		const productsToCalculate = this.getProductsToSaveInvoice(false);
		// setTimeout fixes issue on Safari when id of product changes with delay
		setTimeout(() => {
			if (productsToCalculate && this.currencyAndTax) {
				this.quoteDataService
					.calculateProductsSummary(
						productsToCalculate,
						this.currencyAndTax.percentage,
						this.currencyAndTax.currency.symbol
					)
					.pipe(takeUntil(this.unsubscribe))
					.subscribe(
						data =>
							this.successUpdatePricesCalculation(
								data,
								needToUpdatePayments,
								productsToCalculate
							),
						error => this.messageService.showDefaultErrorMessage(error)
					);
			}
		}, 0);
	}

	private successUpdatePricesCalculation(
		data: Summary,
		needToUpdatePayments,
		productsToCalculate
	): void {
		this.summary = data;
		if (
			this.job &&
			this.invoice &&
			this.paymentScheduleNames &&
			needToUpdatePayments &&
			productsToCalculate.length > 0
		) {
			this.paymentScheduleComponent.getPaymentScheduleNew(
				this.paymentScheduleTemplateId,
				true,
				this.summary.total
			);
		} else if (this.summary && this.summary.total === 0) {
			setTimeout(() => {
				this.paymentScheduleComponent.hideAndResetPaymentSchedule();
			}, 500);
		}
	}

	private successGetCurrencyAndTaxes(data): void {
		this.currencyAndTax = {
			currency: {
				symbol: data.currencyDTO.symbol
			},
			percentage: data.percentage,
			taxName: data.taxName
		};
		this.updateInvoiceSummary(false);
	}

	private setMainInfoData(
		invoiceId: string,
		issueDate: IssueDateFormat,
		poNumber: string
	): void {
		this.mainInfoDataToSave = {
			invoiceId,
			issueDate,
			poNumber
		};
	}

	private addMissDateField(payments: Array<Payment>): Array<Payment> {
		return payments.map((payment: Payment) =>
			Object.assign(payment, { paymentDate: payment.due })
		);
	}

	/* API section and Service call section */

	private getNewInvoice(): void {
		this.jobDataService
			.getQuoteInvoiceJob(this.dataToGetInvoice.id)
			.pipe(
				takeUntil(this.unsubscribe),
				map((res: any): Job => res.data)
			)
			.subscribe((job: Job) => (this.job = job));
	}

	private getCurrencyAndTaxes(): void {
		this.settingsService
			.getCurrencyAndTaxes()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(
				(data): void => {
					this.successGetCurrencyAndTaxes(data);
				},
				(error): void => this.messageService.showDefaultErrorMessage(error)
			);
	}

	private setInvoiceIdAndDate(id): void {
		this.invoicesDataService
			.getInvoiceIdAndDate(id)
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((data: InvoiceIdAndDate): void => {
				this.setMainInfoData(
					data.invoiceId,
					this.quoteUpdateService.setQuoteIssueDate(data.issueDate),
					null
				);
			});
	}

	private getContract() {
		if (this.contract) {
			return this.contractDataService.getContract(
				this.job.id,
				this.contract.id
			);
		}
		return new Observable((observer): void => {
			observer.next(null);
		});
	}

	private getProductsTemplates(): void {
		const pageRequest = QuoteUpdateService.getPageRequest();
		this.productPackagesDataService
			.getPackagesOrProducts(pageRequest)
			.subscribe(
				(data): void => {
					this.setProductsAndDiscountsNames(data.items);
					if (!this.editInvoice) {
						this.addProduct();
					}
				},
				(error): void => this.messageService.showDefaultErrorMessage(error)
			);
	}

	private setProductsAndDiscountsNames(products) {
		const {
			packages,
			addons,
			discounts
		} = QuoteUpdateService.setPackagesAndAddons(products);
		this.productsNames = packages.concat(addons);
		this.discountsNames = discounts;
	}

	private getTimeSettings(): void {
		this.settingsService.timeFormatSettings
			.takeUntil(this.unsubscribe)
			.subscribe((data): void => {
				switch (data.dateFormat) {
					case 'DD_MM_YYYY':
						this.datePlaceholder = 'dd/mm/yyyy';
						break;
					case 'MM_DD_YYYY':
						this.datePlaceholder = 'mm/dd/yyyy';
						break;
					default:
						this.datePlaceholder = '';
						break;
				}
			});
	}

	private getPaymentsSchedules(): void {
		const params = {
			perPage: 500,
			page: 1,
			predicate: 'name',
			reverse: false
		};
		this.paymentsSchedulesDataService
			.getPaymentsSchedules(params)
			.subscribe(
				(data): void => (this.paymentScheduleNames = data.results),
				(error): void => this.messageService.showDefaultErrorMessage(error)
			);
	}

	private getContractAndQuestionnaireInfo(): void {
		Observable.zip(
			this.templatesDataService.getTemplates('contract_preview'),
			this.templatesDataService.getTemplates('questionnaire_preview'),
			this.getContract()
		).subscribe(
			([contractTemplates, questionnaireTemplates, contractData]): void => {
				this.contractQuestionData = {
					contract: contractData ? contractData.contract : null,
					questionnaire: this.questionnaire,
					contractTemplates: contractTemplates.contractTemplates,
					questionnaireTemplates: questionnaireTemplates.questionnaireTemplates
				};
			},
			(error): void => this.messageService.showDefaultErrorMessage(error)
		);
	}

	/* Invoice validation and saving/updating section */

	private validateInvoice(): boolean {
		const dataToValidate = {
			amount: this.summary.subTotal,
			packages: [],
			payments: this.payments,
			discount: this.summary.discountValue,
			xeroStatus: this.invoice.xeroStatus,
			qbStatus: this.invoice.qbStatus,
			totalDue: this.summary.total,
			poNumber: this.mainInfoDataToSave.poNumber,
			pageName: 'invoicePage'
		};
		dataToValidate.packages = this.invoiceService.checkProductPackagesCorrectType(
			this.getProductsToSaveInvoice(false)
		);
		return this.invoiceService.isInvoiceValid(dataToValidate);
	}

	private gatherDataToSaveInvoice(): InvoiceDataToSave {
		return {
			invoiceData: {
				displayId: this.mainInfoDataToSave.invoiceId,
				issueDate: this.timeService.getDateInFormat(
					this.mainInfoDataToSave.issueDate.jsdate,
					'date'
				),
				poNumber: this.mainInfoDataToSave.poNumber,
				jobId: this.jobId,
				paymentScheduleId: this.setPaymentScheduleIdForSave(
					this.paymentScheduleTemplateId
				),
				notes: this.notesToSave,
				packages: this.getProductsToSaveInvoice(true),
				payments: this.getPaymentsToSave()
			},
			removeContract: this.contractToSave === null,
			removeQuestionnaire: this.questionnaireToSaveId === '-1',
			questionnaire: this.getQuestionnaireState()
				? {
						templateId: this.questionnaireToSaveId
				  }
				: null,
			contract:
				this.contractToSave && this.contractToSave.signed
					? null
					: this.getContractToSave()
		};
	}

	private getProductsToSaveInvoice(needToRemoveId): Array<Product> {
		this.products = QuoteUpdateService.setFixedQuoteProducts(this.products);
		const productsToSave = this.products.map((productItem, index): Product =>
			Object.assign(productItem, {
				id: productItem.fromSettings && needToRemoveId ? null : productItem.id,
				order: index,
				price: needToRemoveId
					? this.stringModifierService.convertAmountIntoNumber(
							productItem.price
					  )
					: productItem.price
			})
		);
		this.discounts.forEach((discount, index): void => {
			productsToSave.push(
				Object.assign(discount, {
					id: discount.fromSettings && needToRemoveId ? null : discount.id,
					order: index,
					price: this.stringModifierService.convertAmountIntoNumber(
						discount.price
					),
					taxType: this.setTaxTypeForDiscount(productsToSave, discount.taxType)
				})
			);
		});
		return productsToSave;
	}

	private setTaxTypeForDiscount(productsToSave, previousTax) {
		return productsToSave[0] ? productsToSave[0].taxType : previousTax;
	}

	private setPaymentScheduleIdForSave(id) {
		switch (+id) {
			case 0:
				return this.paymentScheduleIdFromServer;
			case -1:
				return null;
			default:
				return this.paymentScheduleTemplateId;
		}
	}

	private getContractToSave(): Contract {
		if (this.contractToSave) {
			this.contractToSave.name = this.stringModifierService.cutSelectedText(
				this.contractToSave.name
			);
			/* Fix changed field for GDPR */
			if (this.contractToSave.optInConsentStatement) {
				this.contractToSave.optInConsent = this.contractToSave.optInConsentStatement;
				delete this.contractToSave.optInConsentStatement;
			}
			/*
      Fix backend grammar mistake
      showOTPinconsent => showOPTinconsent
       */
			if (this.contractToSave.showOtpInConsent) {
				this.contractToSave.showOptInConsent = this.contractToSave.showOtpInConsent;
				delete this.contractToSave.showOtpInConsen;
			}
		}
		return this.contractToSave;
	}

	private getPaymentsToSave(): Array<Payment> {
		return this.paymentsToSave.map((payment): Payment => {
			let paymentToSave;
			if (payment.date) {
				const date = this.quoteUpdateService.getDate(payment.date.jsdate);
				paymentToSave = Object.assign(payment, {
					paymentDate: date,
					due: date
				});
			} else {
				paymentToSave = payment;
			}
			return paymentToSave;
		});
	}

	private getQuestionnaireState(): boolean {
		let isQuestionnaireChanged = true;
		// Check if questionnaire wasn't changed during invoice update
		if (
			this.questionnaire &&
			+this.questionnaireToSaveId === this.questionnaire.id
		) {
			isQuestionnaireChanged = false;
		}
		// Check if we do not have selected questionnaire on invoice create
		if (
			(!this.questionnaire && !this.questionnaireToSaveId) ||
			this.questionnaireToSaveId === '-1'
		) {
			isQuestionnaireChanged = false;
		}
		return isQuestionnaireChanged;
	}

	private updateInvoice(saveData: InvoiceDataToSave): void {
		this.invoicesDataService
			.updateInvoice(this.dataToGetInvoice.invoiceID, saveData)
			.pipe(
				takeUntil(this.unsubscribe),
				finalize(() => (this.buttonDisable = false))
			)
			.subscribe(
				data => this.successCreateInvoice(data),
				error =>
					this.invoiceService.errorSaveInvoice(
						error,
						this.mainInfoDataToSave.invoiceId
					)
			);
	}

	private createInvoice(saveData: InvoiceDataToSave): void {
		this.invoicesDataService
			.saveNewInvoice(this.jobId, saveData)
			.pipe(
				takeUntil(this.unsubscribe),
				finalize(() => (this.buttonDisable = false))
			)
			.subscribe(
				data => this.successCreateInvoice(data),
				error =>
					this.invoiceService.errorSaveInvoice(
						error,
						this.mainInfoDataToSave.invoiceId
					)
			);
	}

	private successCreateInvoice(data): void {
		this.invoiceService.successCreateInvoice(data).then(id => {
			if (id) {
				this.location.go(`/invoices/${id}`);
			} else {
				this.location.back();
			}
		});
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=kkml25e9zf!./web/modern/components/invoices/invoice-update/invoice-update.ts